import React from 'react'
import { Box, Typography, Card, CardContent, Skeleton, Button, IconButton, alpha, Chip } from '@mui/material'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'

import { WalletTabType, DomainBalance } from '@/src/types/wallet'
import { WALLET_TABS } from '@/src/constants/wallet'
import { getDaysUntilExpiry, formatDisplayDate } from '@/src/utils/wallet'
import WalletEmptyStateWrapper from '@/src/components/common/wallet/walletEmptyState'

export interface FormattedTransaction {
    id: string
    source: string
    reference: string | null
    amount: number
    currency: string
    expiryDate: string
    transactionDate: string
    rawSourceType: string
    iconType: 'refresh' | 'gift' | 'card'
}

interface WalletPageProps {
    // Balance data
    displayBalance: number
    displayCurrency: string
    domainBalances: DomainBalance[]
    hasMultipleDomainBalances: boolean

    // Transactions data
    formattedTransactions: FormattedTransaction[]
    hasMoreTransactions: boolean

    // Tab state
    activeTab: WalletTabType
    onTabChange: (tab: WalletTabType) => void

    // Loading states
    isLoadingBalance: boolean
    isLoadingTransactions: boolean
    isFetchingMore: boolean

    // Error states
    balanceError: string | null
    transactionsError: string | null

    // Actions
    onLoadMore: () => void
    onRefresh: () => void
    onGoToHome?: () => void
    showBackButton?: boolean

    // Domain balance sheet
    isDomainBalanceSheetOpen: boolean
    onOpenDomainBalanceSheet: () => void
    onCloseDomainBalanceSheet: () => void

    // FAQ drawer
    isFAQDrawerOpen?: boolean
    onOpenFAQDrawer?: () => void
    onCloseFAQDrawer?: () => void

    // Formatting
    formatAmount: (amount: number, currency: string) => string

    // Translations
    t: (key: string, values?: Record<string, any>) => string
    tCommon: (key: string) => string

    // Locale
    locale: string
}

const WalletPage: React.FC<WalletPageProps> = ({
    displayBalance,
    displayCurrency,
    domainBalances,
    hasMultipleDomainBalances,
    formattedTransactions,
    hasMoreTransactions,
    activeTab,
    onTabChange,
    isLoadingBalance,
    isLoadingTransactions,
    isFetchingMore,
    balanceError,
    transactionsError,
    onLoadMore,
    onRefresh,
    onGoToHome,
    showBackButton = false,
    isDomainBalanceSheetOpen,
    onOpenDomainBalanceSheet,
    onCloseDomainBalanceSheet,
    isFAQDrawerOpen = false,
    onOpenFAQDrawer,
    onCloseFAQDrawer,
    formatAmount,
    t,
    tCommon,
    locale
}) => {
    // Helper to get icon component based on icon type (presentation logic only)
    const getIconComponent = (iconType: 'refresh' | 'gift' | 'card') => {
        switch (iconType) {
            case 'refresh':
                return RefreshIcon
            case 'gift':
                return CardGiftcardOutlinedIcon
            case 'card':
            default:
                return CreditCardOutlinedIcon
        }
    }

    return (
        <Box
            sx={{
                bgcolor: 'background.default',
                pb: 10
            }}
        >
            {/* Page Title */}
            <Box
                sx={{
                    px: 2,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                }}
            >
                {showBackButton && onGoToHome && (
                    <IconButton
                        onClick={onGoToHome}
                        size="small"
                        sx={{
                            position: 'absolute',
                            left: 8,
                            color: 'primary.main'
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                )}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('title')}
                </Typography>
            </Box>

            {/* Balance Card */}
            <Box sx={{ px: 0 }}>
                <Card
                    elevation={0}
                    sx={{
                        background: 'white',
                        color: 'text.primary',
                        borderRadius: 0
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <AccountBalanceWalletIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                {t('walletBalance')}
                            </Typography>
                            {!balanceError && domainBalances && domainBalances.length > 0 && (
                                <IconButton
                                    size="small"
                                    onClick={onOpenDomainBalanceSheet}
                                    sx={{ color: 'grey.500', ml: 'auto' }}
                                >
                                    <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>

                        {isLoadingBalance ? (
                            <Skeleton variant="text" width={150} height={48} sx={{ bgcolor: 'grey.200' }} />
                        ) : balanceError ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {balanceError}
                            </Typography>
                        ) : (
                            <>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontSize: '1.8rem',
                                        fontWeight: 700,
                                        letterSpacing: '-0.5px',
                                        mb: 1,
                                        color: 'text.primary'
                                    }}
                                >
                                    {formatAmount(displayBalance, displayCurrency)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                        display: 'block'
                                    }}
                                >
                                    {t('usableOnlyInCurrency', { currency: displayCurrency })}
                                </Typography>
                            </>
                        )}
                    </CardContent>
                </Card>
            </Box>

            {/* About Wallet Block */}
            {onOpenFAQDrawer && (
                <Box
                    onClick={onOpenFAQDrawer}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 1.5,
                        bgcolor: 'background.paper',
                        borderTop: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': {
                            bgcolor: 'action.hover'
                        }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: 'grey.200',
                            color: 'text.secondary',
                            mr: 1.5
                        }}
                    >
                        <HelpOutlineIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            flex: 1,
                            fontWeight: 600,
                            color: 'text.primary'
                        }}
                    >
                        {t('aboutWallet')}
                    </Typography>
                    <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
                </Box>
            )}

            {/* Wallet Activity Heading */}
            <Box sx={{ px: 2, pt: 3, pb: 1.5 }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '1.125rem'
                    }}
                >
                    {t('walletActivity')}
                </Typography>
            </Box>

            {/* Tabs */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    px: 2,
                    py: 0.5,
                    bgcolor: 'background.paper'
                }}
            >
                {WALLET_TABS.map(tab => (
                    <Button
                        key={tab}
                        variant={activeTab === tab ? 'contained' : 'text'}
                        size="small"
                        onClick={() => onTabChange(tab)}
                        sx={{
                            borderRadius: 2,
                            px: 2,
                            py: 0.75,
                            fontWeight: activeTab === tab ? 600 : 500,
                            textTransform: 'capitalize',
                            bgcolor: activeTab === tab ? 'primary.main' : 'transparent',
                            color: activeTab === tab ? 'white' : 'text.secondary',
                            '&:hover': {
                                bgcolor:
                                    activeTab === tab
                                        ? 'primary.dark'
                                        : theme => alpha(theme.palette.primary.main, 0.08)
                            }
                        }}
                    >
                        {t(`tabs.${tab.toLowerCase()}`)}
                    </Button>
                ))}
            </Box>

            {/* Transactions List */}
            <Box sx={{ px: 2, pt: 2 }}>
                {isLoadingTransactions && !isFetchingMore ? (
                    // Loading skeleton
                    Array.from({ length: 3 }).map((_, i) => (
                        <Card
                            key={i}
                            elevation={0}
                            sx={{
                                mb: 1.5,
                                bgcolor: 'background.paper',
                                borderRadius: 2,
                                border: 1,
                                borderColor: 'divider'
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: 2.5,
                                    '&:last-child': {
                                        pb: 1.75
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    {/* Icon Shimmer */}
                                    <Skeleton
                                        variant="circular"
                                        width={48}
                                        height={48}
                                        sx={{ bgcolor: 'grey.200', flexShrink: 0 }}
                                    />

                                    {/* Content - Title and Date */}
                                    <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                        <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.75 }} />
                                        <Skeleton variant="text" width="50%" height={16} />
                                    </Box>

                                    {/* Amount and Expiry - Right Side */}
                                    <Box sx={{ textAlign: 'right', flexShrink: 0, pt: 0.5 }}>
                                        <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
                                        <Skeleton
                                            variant="rectangular"
                                            width={100}
                                            height={22}
                                            sx={{ borderRadius: '12px', ml: 'auto' }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))
                ) : transactionsError ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 6,
                            color: 'text.secondary'
                        }}
                    >
                        <Typography variant="body2">{transactionsError}</Typography>
                        <Button variant="text" size="small" onClick={onRefresh} sx={{ mt: 1 }}>
                            {tCommon('retry')}
                        </Button>
                    </Box>
                ) : formattedTransactions.length === 0 ? (
                    <WalletEmptyStateWrapper />
                ) : (
                    <>
                        {formattedTransactions.map((txn, index) => {
                            const daysUntilExpiry = activeTab !== 'USED' ? getDaysUntilExpiry(txn.expiryDate) : null
                            const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 10
                            const displayDate = formatDisplayDate(
                                activeTab === 'USED' ? txn.transactionDate : txn.transactionDate
                            )

                            return (
                                <Card
                                    key={txn.id}
                                    elevation={0}
                                    sx={{
                                        mb: 1.5,
                                        bgcolor: 'background.paper',
                                        borderRadius: 2,
                                        border: 1,
                                        borderColor: 'divider'
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            p: 2.5,
                                            '&:last-child': {
                                                pb: 1.75
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                alignItems: 'flex-start'
                                            }}
                                        >
                                            {/* Icon */}
                                            {(() => {
                                                const IconComponent = getIconComponent(txn.iconType)
                                                return (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: 48,
                                                            height: 48,
                                                            borderRadius: '50%',
                                                            bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        <IconComponent
                                                            sx={{
                                                                fontSize: 28,
                                                                color: 'primary.main'
                                                            }}
                                                        />
                                                    </Box>
                                                )
                                            })()}

                                            {/* Content - Title and Date */}
                                            <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 700,
                                                        mb: 0.75,
                                                        fontSize: '0.9375rem',
                                                        lineHeight: 1.4,
                                                        color: 'text.primary'
                                                    }}
                                                >
                                                    {txn.source}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontSize: '0.8125rem',
                                                        display: 'block'
                                                    }}
                                                >
                                                    {displayDate}
                                                </Typography>
                                            </Box>

                                            {/* Amount and Expiry - Right Side */}
                                            <Box sx={{ textAlign: 'right', flexShrink: 0, pt: 0.5 }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color:
                                                            activeTab === 'EARNED' || activeTab === 'REFUND'
                                                                ? 'success.main'
                                                                : 'error.main',
                                                        fontSize: '1rem',
                                                        mb: 1,
                                                        lineHeight: 1.2
                                                    }}
                                                >
                                                    {activeTab === 'EARNED' || activeTab === 'REFUND' ? '+' : '-'}
                                                    {formatAmount(txn.amount, txn.currency)}
                                                </Typography>

                                                {/* Expiry Chip */}
                                                {activeTab !== 'USED' && txn.expiryDate && txn.expiryDate !== '-' && (
                                                    <Chip
                                                        label={
                                                            daysUntilExpiry !== null && daysUntilExpiry <= 10
                                                                ? daysUntilExpiry === 1
                                                                    ? t('expiresInDays', { days: daysUntilExpiry })
                                                                    : t('expiresInDaysPlural', {
                                                                          days: daysUntilExpiry
                                                                      })
                                                                : t('validTill', {
                                                                      date: formatDisplayDate(txn.expiryDate)
                                                                  })
                                                        }
                                                        size="small"
                                                        sx={{
                                                            height: 22,
                                                            fontSize: '0.6875rem',
                                                            fontWeight: 500,
                                                            bgcolor: isExpiringSoon
                                                                ? theme => alpha(theme.palette.warning.main, 0.15)
                                                                : 'grey.200',
                                                            color: isExpiringSoon ? 'warning.main' : 'text.secondary',
                                                            borderRadius: '12px',
                                                            '& .MuiChip-label': {
                                                                px: 1.25,
                                                                py: 0.25
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Transaction Details - Subtle at bottom */}
                                        <Box
                                            sx={{
                                                mt: 1.5,
                                                pt: 1.5,
                                                borderTop: 1,
                                                borderColor: 'divider',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            {txn.reference && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontSize: '0.6875rem'
                                                    }}
                                                >
                                                    {txn.reference}
                                                </Typography>
                                            )}
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'text.disabled',
                                                    fontSize: '0.6875rem',
                                                    fontFamily: 'monospace',
                                                    ml: 'auto'
                                                }}
                                            >
                                                {t('transactionId')}: {txn.id}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            )
                        })}

                        {/* Load More Button */}
                        {hasMoreTransactions && (
                            <Box sx={{ textAlign: 'center', pt: 2, pb: 4 }}>
                                <Button
                                    variant="outlined"
                                    onClick={onLoadMore}
                                    disabled={isFetchingMore}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 500
                                    }}
                                >
                                    {isFetchingMore ? t('loading') : t('loadMore')}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
}

export { WalletPage }
export type { WalletPageProps }
